/**
 * src/lib/voice/echoCorrelation.js — TICKET-623
 *
 * Known-reference envelope correlation for the barge-in VAD. Root cause of
 * self-interruption on iOS Safari: WebAudio destination output gets no
 * OS-level echo cancellation, so the assistant's own TTS leaks into the
 * barge-in mic stream and the VAD reads it as the user interrupting.
 *
 * The fix in useVoiceBargeIn.js's docblock originally sketched a full AEC
 * loopback (capture speaker output via MediaStreamDestinationNode, subtract
 * in an AudioWorkletProcessor). That's solving a HARDER problem than we
 * have: we already know the EXACT reference signal — it's the PCM buffer
 * pcmPlayer.js is scheduling, in JS memory, at the moment it plays. No
 * adaptive filter needs to learn what's playing.
 *
 * So instead of subtracting an unknown captured echo, this module compares
 * the mic's RMS envelope shape against the KNOWN-playing PCM's RMS envelope
 * over a short rolling window. High correlation (right shape, plausible
 * lag) = echo, gate it. Low correlation = a foreign voice = genuine
 * barge-in. Pure array math — no AudioContext, no DOM — so it's testable
 * with recorded/synthetic fixtures without a live device loop.
 *
 * Fails OPEN by design: no reference pushed yet (legacy buffered TTS path
 * has no pcmPlayer) or not enough samples yet → never classified as echo.
 * A stuck/wrong detector must never permanently silence a real barge-in
 * (DEBUG LADDER: worse to over-suppress the user than to occasionally miss
 * an echo frame — the outer rolling-floor gate in voiceActivityDetector.js
 * still applies underneath this).
 */

/** Pearson correlation coefficient of two equal-length numeric arrays. */
export function pearsonCorrelation(a, b) {
  if (a.length !== b.length) throw new Error('pearsonCorrelation: arrays must be equal length');
  const n = a.length;
  if (n === 0) return 0;
  const meanA = a.reduce((s, v) => s + v, 0) / n;
  const meanB = b.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let sumSqA = 0;
  let sumSqB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA;
    const db = b[i] - meanB;
    num += da * db;
    sumSqA += da * da;
    sumSqB += db * db;
  }
  const denom = Math.sqrt(sumSqA * sumSqB);
  if (denom === 0) return 0; // zero-variance (flat) input — undefined correlation, not NaN
  return num / denom;
}

const DEFAULT_DT_MS = 20;

/**
 * Resample {rms,t}[] samples (t in seconds) onto a fixed-rate grid covering
 * [startMs, endMs] every dtMs, using nearest-preceding-sample hold. Samples
 * must already be sorted by t (callers push in time order).
 */
function resampleToGrid(samples, startMs, endMs, dtMs) {
  const out = [];
  if (samples.length === 0) return out;
  let idx = 0;
  for (let tMs = startMs; tMs <= endMs; tMs += dtMs) {
    const tSec = tMs / 1000;
    while (idx + 1 < samples.length && samples[idx + 1].t <= tSec) idx++;
    out.push(samples[idx].t <= tSec ? samples[idx].rms : samples[0].rms);
  }
  return out;
}

/**
 * Correlate a mic envelope against a known-reference envelope, searching
 * lags in [-maxLagMs, +maxLagMs] for the best Pearson correlation (the mic
 * signal is expected to lag the reference by the speaker→mic acoustic path
 * + buffering delay, typically tens of ms).
 *
 * @param {{rms:number,t:number}[]} micSamples
 * @param {{rms:number,t:number}[]} refSamples
 * @returns {{correlation:number, lagMs:number}}
 */
export function correlateEnvelopes(micSamples, refSamples, { dtMs = DEFAULT_DT_MS, maxLagMs = 200, minOverlapMs } = {}) {
  if (micSamples.length === 0 || refSamples.length === 0) return { correlation: 0, lagMs: 0 };

  const micStartMs = micSamples[0].t * 1000;
  const micEndMs = micSamples[micSamples.length - 1].t * 1000;
  const refStartMs = refSamples[0].t * 1000;
  const refEndMs = refSamples[refSamples.length - 1].t * 1000;

  // Overlap window (before lag search) — if the two windows don't overlap
  // at all even allowing for maxLagMs, there's nothing to correlate.
  const overlapStart = Math.max(micStartMs, refStartMs) - maxLagMs;
  const overlapEnd = Math.min(micEndMs, refEndMs) + maxLagMs;
  if (overlapEnd - overlapStart < dtMs * 3) return { correlation: 0, lagMs: 0 };

  // See estimateEchoResidual for why this guard exists (toyota tuning sweep
  // caught a shrinking-window edge artifact at lag=±maxLagMs).
  const shortestSpanMs = Math.min(micEndMs - micStartMs, refEndMs - refStartMs);
  const requiredOverlapMs = minOverlapMs ?? Math.max(dtMs * 3, shortestSpanMs * 0.7);

  let best = { correlation: 0, lagMs: 0 };
  for (let lagMs = -maxLagMs; lagMs <= maxLagMs; lagMs += dtMs) {
    // Shift the mic samples back by lagMs before resampling onto the shared
    // grid, so a positive lag means "mic follows reference by lagMs".
    const shiftedMic = micSamples.map((s) => ({ rms: s.rms, t: s.t - lagMs / 1000 }));
    const gridStart = Math.max(shiftedMic[0].t * 1000, refSamples[0].t * 1000);
    const gridEnd = Math.min(shiftedMic[shiftedMic.length - 1].t * 1000, refSamples[refSamples.length - 1].t * 1000);
    if (gridEnd - gridStart < requiredOverlapMs) continue;
    const micGrid = resampleToGrid(shiftedMic, gridStart, gridEnd, dtMs);
    const refGrid = resampleToGrid(refSamples, gridStart, gridEnd, dtMs);
    if (micGrid.length < 3 || micGrid.length !== refGrid.length) continue;
    const corr = pearsonCorrelation(micGrid, refGrid);
    if (corr > best.correlation) best = { correlation: corr, lagMs };
  }
  return best;
}

/**
 * Best-fit single-tap echo model: search lags for the strongest correlation,
 * then at that lag estimate a scalar gain g (least squares: mic ≈ g·ref) and
 * the RESIDUAL energy mic - g·ref leaves behind.
 *
 * Why residual energy and not raw shape correlation: toyota self-test
 * (scripts/voice/tune-echo-gate.mjs) proved raw correlation is dangerous —
 * a mic signal that's echo-plus-real-voice still correlates highly with the
 * echo shape (addition doesn't destroy the underlying shape match), so 32/64
 * synthetic overlapping-barge-in scenarios were misclassified as pure echo
 * at correlation>=0.6. That would have shipped a gate that silences genuine
 * interruptions — a worse bug than the one this ticket fixes.
 * Residual energy asks a different question: "how much of the mic signal is
 * LEFT OVER after removing the best possible echo explanation?" A foreign
 * voice on top of the echo leaves real residual energy behind; pure echo
 * (any gain, any lag) explains nearly all of the mic energy and leaves
 * almost none.
 *
 * @returns {{gain:number, lagMs:number, correlation:number, residualRatio:number}}
 *   residualRatio = sum((mic-g·ref)^2) / sum(mic^2), in [0,1]-ish (can exceed
 *   1 if the residual amplifies past mic energy, e.g. wildly wrong gain).
 */
export function estimateEchoResidual(micSamples, refSamples, { dtMs = DEFAULT_DT_MS, maxLagMs = 200, minOverlapMs } = {}) {
  const none = { gain: 0, lagMs: 0, correlation: 0, residualRatio: 1, residualRms: Infinity };
  if (micSamples.length === 0 || refSamples.length === 0) return none;

  const micStartMs = micSamples[0].t * 1000;
  const micEndMs = micSamples[micSamples.length - 1].t * 1000;
  const refStartMs = refSamples[0].t * 1000;
  const refEndMs = refSamples[refSamples.length - 1].t * 1000;
  const overlapStart = Math.max(micStartMs, refStartMs) - maxLagMs;
  const overlapEnd = Math.min(micEndMs, refEndMs) + maxLagMs;
  if (overlapEnd - overlapStart < dtMs * 3) return none;

  // GUARD (toyota tuning, scripts/voice/tune-echo-gate.mjs): a shrinking
  // overlap window near the lag-search boundary lets a scalar gain fit a
  // handful of leftover points almost perfectly by chance, faking a
  // near-zero residual at lag=±maxLagMs. Require most of the shorter
  // window to actually overlap before a candidate lag is even considered —
  // kills that false "perfect fit at the edge" artifact.
  const shortestSpanMs = Math.min(micEndMs - micStartMs, refEndMs - refStartMs);
  const requiredOverlapMs = minOverlapMs ?? Math.max(dtMs * 3, shortestSpanMs * 0.7);

  let best = null;
  for (let lagMs = -maxLagMs; lagMs <= maxLagMs; lagMs += dtMs) {
    const shiftedMic = micSamples.map((s) => ({ rms: s.rms, t: s.t - lagMs / 1000 }));
    const gridStart = Math.max(shiftedMic[0].t * 1000, refSamples[0].t * 1000);
    const gridEnd = Math.min(shiftedMic[shiftedMic.length - 1].t * 1000, refSamples[refSamples.length - 1].t * 1000);
    if (gridEnd - gridStart < requiredOverlapMs) continue;
    const micGrid = resampleToGrid(shiftedMic, gridStart, gridEnd, dtMs);
    const refGrid = resampleToGrid(refSamples, gridStart, gridEnd, dtMs);
    if (micGrid.length < 3 || micGrid.length !== refGrid.length) continue;
    const corr = pearsonCorrelation(micGrid, refGrid);
    if (!best || corr > best.correlation) {
      // Least-squares scalar gain: g minimizing sum((mic - g*ref)^2).
      let dotMicRef = 0;
      let dotRefRef = 0;
      let sumSqMic = 0;
      for (let i = 0; i < micGrid.length; i++) {
        dotMicRef += micGrid[i] * refGrid[i];
        dotRefRef += refGrid[i] * refGrid[i];
        sumSqMic += micGrid[i] * micGrid[i];
      }
      const gain = dotRefRef > 0 ? dotMicRef / dotRefRef : 0;
      let sumSqResidual = 0;
      for (let i = 0; i < micGrid.length; i++) {
        const residual = micGrid[i] - gain * refGrid[i];
        sumSqResidual += residual * residual;
      }
      const residualRatio = sumSqMic > 0 ? sumSqResidual / sumSqMic : 1;
      const residualRms = Math.sqrt(sumSqResidual / micGrid.length);
      best = { gain, lagMs, correlation: corr, residualRatio, residualRms };
    }
  }
  return best || none;
}

const RING_BUFFER_MS = 200; // short window — a long buffer dilutes a mid-window user-onset (toyota sweep)

/**
 * Stateful gate: feed it the barge-in mic's RMS ticks and the TTS player's
 * known-reference RMS ticks; ask isLikelyEcho() to decide whether the
 * current mic activity is the assistant hearing itself.
 *
 * Decision metric is residualRMS, not residualRatio (toyota sweep, round 2):
 * ratio-to-mic's-own-energy shrinks artificially when a loud real voice is
 * mixed in — the denominator inflates right along with the thing we're
 * trying to detect, hiding it. residualRMS is the ABSOLUTE leftover energy
 * after removing the best-fit echo, so it composes with the EXISTING
 * rolling-floor threshold system (rollingNoiseFloor.js) instead of
 * inventing a second independent magic number: "is what's left over, after
 * explaining away everything the echo model can, still loud enough by
 * itself to be real speech?" residualThreshold defaults to the VAD's own
 * absoluteMin (0.02) scaled by the same thresholdMultiplier used elsewhere
 * (BARGE_IN_VAD_TUNING) so one tuning knob governs both paths.
 */
export function createEchoGate({
  dtMs = DEFAULT_DT_MS,
  maxLagMs = 200,
  // toyota tuning sweep (scripts/voice/tune-echo-gate.mjs) found the two
  // classes cleanly separate in [0.0203, 0.0267] residualRms across 80
  // synthetic scenarios (attenuation × lag × overlap-timing); 0.022 sits
  // centered in that band with margin both directions.
  residualThreshold = 0.022,
  minSamples = 6,
} = {}) {
  let micBuf = [];
  let refBuf = [];

  const trim = (buf, nowT) => buf.filter((s) => nowT - s.t <= RING_BUFFER_MS / 1000);

  return {
    pushMic(rms, t) {
      micBuf.push({ rms, t });
      micBuf = trim(micBuf, t);
    },
    pushReference(rms, t) {
      refBuf.push({ rms, t });
      refBuf = trim(refBuf, t);
    },
    isLikelyEcho() {
      if (refBuf.length === 0) return { isEcho: false, reason: 'no_reference', residualRms: Infinity, correlation: 0, lagMs: 0 };
      if (micBuf.length < minSamples || refBuf.length < minSamples) {
        return { isEcho: false, reason: 'insufficient_data', residualRms: Infinity, correlation: 0, lagMs: 0 };
      }
      const { correlation, lagMs, residualRms } = estimateEchoResidual(micBuf, refBuf, { dtMs, maxLagMs });
      return { isEcho: residualRms <= residualThreshold, reason: 'residual', correlation, lagMs, residualRms };
    },
    reset() {
      micBuf = [];
      refBuf = [];
    },
  };
}
